import { useGetFormConfigQuery } from '@/features/config/configApi';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Avatar,
	Box,
	Button,
	Chip,
	Stack,
	Typography,
} from '@mui/material';
import { useFormikContext } from 'formik';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Contact from './Contact';
import { useClientSchemaContext } from '@/context/clientSchemaContext';
import { VIEW_TYPES, isProspectFieldVisible } from '@/features/config/helper';
import { openDialog } from '@/features/dialog/dialogSlice';

const GROUP = 'contact';

const ContactAccordion = ({ contact, index, expanded, setExpanded, children }) => {
	return (
		<Accordion
			key={`contact-accordion-${index}`}
			expanded={expanded === index}
			onChange={setExpanded(index)}
			sx={{ background: 'none', boxShadow: 'none' }}
		>
			<AccordionSummary
				expandIcon={<ExpandMoreIcon />}
				aria-controls={`panel-${index}-content`}
				id={`panel-${index}-header`}
				sx={{ background: 'none' }}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 2,
					}}
				>
					<Box
						sx={{
							position: 'relative',
						}}
					>
						<Avatar sx={{ width: 48, height: 48 }}>
							<AccountCircleIcon sx={{ fontSize: 32, color: 'white' }} />
						</Avatar>
						{index > 0 && !contact?.Id && (
							<Chip
								size='small'
								color='secondary'
								label='NEW'
								sx={{
									position: 'absolute',
									top: 2,
									left: '-30%',
									height: '20px',

									'& .MuiChip-label': {
										fontSize: '12px',
										px: '5px',
									},
								}}
							/>
						)}
					</Box>

					<Box>
						<Stack direction='row' alignItems='center' spacing={1}>
							<Typography variant='h6' fontWeight='bold'>
								{contact?.Contact}
							</Typography>

							{contact?.isMainContact && (
								<Chip label='Primary contact' color='primary' size='small' />
							)}
						</Stack>

						{contact?.Email && (
							<Typography variant='body2' color='textSecondary'>
								{contact?.Email}
							</Typography>
						)}
					</Box>
				</Box>
			</AccordionSummary>
			<AccordionDetails sx={{ background: 'none' }}>{children}</AccordionDetails>
		</Accordion>
	);
};

export const ContactField = ({
	contact,
	index,
	view,
	variant,
	fields,
	expanded,
	setExpanded,
	disabled,
}) => {
	const accordionView = !(view === VIEW_TYPES.create || variant === 'editContact');
	const Wrapper = accordionView ? ContactAccordion : React.Fragment;
	return (
		<Stack spacing={1}>
			<Wrapper
				{...(accordionView && {
					contact,
					index,
					expanded,
					setExpanded,
				})}
			>
				<Contact {...{ fields, index, disabled }} />
			</Wrapper>
		</Stack>
	);
};

const ContactFields = ({ disabled }) => {
	const dispatch = useDispatch();
	const { values } = useFormikContext();
	const { view, variant, isProspect } = useClientSchemaContext();

	const [expanded, setExpanded] = useState(false);
	const handleAccordionChange = (index) => (event, isExpanded) => {
		setExpanded(isExpanded ? index : null);
	};

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const fieldsQuery = useGetFormConfigQuery(
		{ configType: 'client' },
		{
			skip: !userDetails,
			selectFromResult: ({ data, ...rest }) => ({
				...rest,
				data:
					data?.filter(
						(f) =>
							f.group === GROUP &&
							(isProspect ? isProspectFieldVisible(f, view) : f.visible?.[view]),
					) ?? [],
			}),
		},
	);

	const fields = useMemo(() => {
		const data = (fieldsQuery.data ?? []).slice();
		return data.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
	}, [fieldsQuery.data]);

	const contacts = values.contacts;

	if (fields.length === 0) return <></>;

	return (
		<Stack spacing={2}>
			<Stack
				direction='row'
				justifyContent={view === VIEW_TYPES.create ? 'flex-start' : 'space-between'}
				alignItems={'center'}
			>
				{variant !== 'editContact' && (
					<Typography variant='form_label'>
						{view === VIEW_TYPES.create ? 'Contacts' : 'Contact details'}
					</Typography>
				)}
				{view === VIEW_TYPES.update && variant !== 'editContact' && (
					<Button
						variant='tab_header'
						size='small'
						onClick={() => dispatch(openDialog('contact'))}
						disabled={disabled}
						startIcon={<AddIcon />}
					>
						Add contact
					</Button>
				)}
			</Stack>
			<Stack spacing={1}>
				{contacts.map((contact, index) => (
					<ContactField
						key={`contact-${index}`}
						{...{
							contact,
							index,
							view,
							variant,
							fields,
							expanded,
							setExpanded: handleAccordionChange,
							disabled,
						}}
					/>
				))}
			</Stack>
		</Stack>
	);
};
export default ContactFields;
